// TO BE DEPRECATED AFTER MIGRATING TO NUXT / VUE 3 PAGE ROUTING SYSTEM
import { useCtxStore } from '@/stores/ctx.store'
import consola from 'consola'
import { defineNuxtPlugin } from 'nuxt/app'
import { useAppMachine } from '~/composables/useAppMachine'
import { urlCtxPartObj, urlParamPartObj } from '~/types/route.entity'
import type { DisplayMode, FstmtView, SectionKey } from '~/types/stcs_wapp_ctx_data.entity'
import { navigateTo, useRouter } from '#imports'

const logger = consola.withTag('[router.plugin.ts]')

/**
 * Handles only sections of the app. No Auth or other global stuff should be handled here.
 * @name router.plugin
 */
export default defineNuxtPlugin(() => {
  const router = useRouter()
  router.beforeEach((to, _from) => {
    // Determine section from path (more reliable than route name for nested routes)
    const getSectionFromPath = (path: string): SectionKey | null => {
      if (path.includes('/dashboard')) return 'dashboard'
      if (path.includes('/financials')) return 'financials'
      if (path.includes('/analysis')) return 'analysis'
      if (path.includes('/plan')) return 'plan'
      if (path.includes('/settings')) return 'settings'
      return null
    }

    const section_key = getSectionFromPath(to.path)

    // Early return if not a section route
    if (!section_key) {
      return
    }

    logger.debug('router.plugin detected section_key from path:', section_key)

    const pathParts = to.path.slice(1).split('/')

    // Check if path contains /comp/ segment to determine if we should extract compId
    const hasCompSegment = pathParts[2] === 'comp'

    const pathOrgId = (): string => {
      if (pathParts[1]) {
        useCtxStore().active_org_id = pathParts[1]
        logger.debug('Updated active_org_id from path:', useCtxStore().active_org_id)
      }
      return useCtxStore().active_org_id
    }
    const pathCompId = (): string => {
      // Only update active_comp_id if path has /comp/:compId structure
      if (hasCompSegment && pathParts[3]) {
        useCtxStore().active_comp_id = pathParts[3]
        logger.debug('Updated active_comp_id from path:', useCtxStore().active_comp_id)
      }
      return useCtxStore().active_comp_id
    }

    // Only validate company if we're on a route that requires it
    if (hasCompSegment && !useCtxStore().ref_comp_map[pathCompId()]) {
      logger.error('🔴 router.pluing.ts | Comp not found in the store, redirecting to the org page.')
      navigateTo(urlCtxPartObj.ORG.path)
    }

    // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
    Object.keys(to.query).forEach((key_name) => {
      logger.debug(`router.plugin Query parameter: ${key_name} = ${to.query[key_name]}`)
      if (key_name === urlParamPartObj.lang.key_name) {
        useAppMachine().setLanguage(to.query[key_name] as string)
      }

      // Only update ui_map params if we have a valid company context
      if (hasCompSegment && useCtxStore().ref_comp_map[pathCompId()]) {
        if (key_name === urlParamPartObj.actuals.key_name) {
          useCtxStore().ref_comp_map[pathCompId()].ui_map[section_key].scope_params.actuals_date_str = to.query[key_name] as string
        }

        if (key_name === urlParamPartObj.plan.key_name) {
          useCtxStore().ref_comp_map[pathCompId()].ui_map[section_key].scope_params.plan_date_str = to.query[key_name] as string
        }

        if (key_name === urlParamPartObj.range.key_name) {
          useCtxStore().ref_comp_map[pathCompId()].ui_map[section_key].scope_params.range_date_str = to.query[key_name] as string
        }
      }
    })

    if (hasCompSegment && ['financials', 'analysis', 'plan'].includes(section_key)) {
      // monthly, yearly, diff
      const pathDisplayMode = (): DisplayMode => {
        const displayMode = useCtxStore().ref_comp_map[pathCompId()].ui_map[section_key].display_mode
        logger.debug('Existing displayMode:', displayMode)

        if (pathParts[5]) {
          useCtxStore().ref_comp_map[pathCompId()].ui_map[section_key].display_mode = pathParts[5] as DisplayMode
          logger.debug('Updated displayMode from path:', useCtxStore().ref_comp_map[pathCompId()].ui_map[section_key].display_mode)
        } else {
          useCtxStore().ref_comp_map[pathCompId()].ui_map[section_key].display_mode = displayMode ?? ('monthly' as DisplayMode)
          logger.debug('Retained existing displayMode:', useCtxStore().ref_comp_map[pathCompId()].ui_map[section_key].display_mode)
        }
        logger.debug('Final displayMode to be used:', useCtxStore().ref_comp_map[pathCompId()].ui_map[section_key].display_mode)
        return useCtxStore().ref_comp_map[pathCompId()].ui_map[section_key].display_mode as DisplayMode
      }

      // pnl, bs, cf
      const pathFfstmt = (): FstmtView => {
        const fstmtView = useCtxStore().ref_comp_map[pathCompId()].ui_map[section_key].fstmt_view
        logger.debug('Existing fstmtView:', fstmtView)

        if (pathParts[6]) {
          useCtxStore().ref_comp_map[pathCompId()].ui_map[section_key].fstmt_view = pathParts[6] as FstmtView
          logger.debug('Updated fstmtView from path:', useCtxStore().ref_comp_map[pathCompId()].ui_map[section_key].fstmt_view)
        } else {
          useCtxStore().ref_comp_map[pathCompId()].ui_map[section_key].fstmt_view = fstmtView ?? ('pnl' as FstmtView)
          logger.debug('Retained existing fstmtView:', useCtxStore().ref_comp_map[pathCompId()].ui_map[section_key].fstmt_view)
        }
        logger.debug('Final fstmtView to be used:', useCtxStore().ref_comp_map[pathCompId()].ui_map[section_key].fstmt_view)
        return useCtxStore().ref_comp_map[pathCompId()].ui_map[section_key].fstmt_view as FstmtView
      }

      logger.debug(`router.plugin pathParts: ${pathParts}`)
      logger.debug('Org ID from path:', pathOrgId())
      logger.debug('Comp ID from path:', pathCompId())
      logger.debug('Display Mode from path:', pathDisplayMode())
      logger.debug('Fstmt View from path:', pathFfstmt())

      pathOrgId()
      pathCompId()
      pathDisplayMode()
      pathFfstmt()
    }

    useCtxStore().setActiveSection(section_key)
  })
})
